import { defineStore } from 'pinia';
import axiosApiInstance from '@/helpers/api.axios.headers';
import type { UserData, UserState } from '@/types/user.interfaces';

/* one in-flight refresh per app boot, shared by everyone who awaits it */
let refreshPromise: Promise<void> | null = null;

export const useUserStore = defineStore('user', {
    state: (): UserState<UserData> => ({
        user:
            localStorage.getItem('userData') != null
                ? JSON.parse(String(localStorage.getItem('userData')))
                : null,
    }),
    actions: {
        setUser(userData: UserData) {
            this.user = userData;
            localStorage.setItem('userData', JSON.stringify(userData));
        },
        clearUser() {
            this.user = null;
            localStorage.removeItem('userData');
        },
        /* re-pulls identity + capability flags (canManualSync, discountsEnabled);
           gates that depend on a fresh flag await this instead of trusting localStorage */
        refreshUser(): Promise<void> {
            if (!refreshPromise) {
                refreshPromise = axiosApiInstance
                    .get('/userData')
                    .then((response) => {
                        if (response.data && response.data.status) {
                            this.setUser({ ...response.data.userData });
                        }
                    })
                    .catch(() => {});
            }
            return refreshPromise;
        },
    },
    getters: {
        userEmail: (state: { user: { userEmail: string } | null }) =>
            state.user != null ? state.user.userEmail : null,
        userName: (state: { user: { userRealName: string } | null }) =>
            state.user != null ? state.user.userRealName : null,
        userId: (state: { user: { userId: string } | null }) =>
            state.user != null ? state.user.userId : null,
        canManualSync: (state: { user: { canManualSync?: boolean } | null }) =>
            state.user != null ? state.user.canManualSync === true : false,
        discountsEnabled: (state: { user: { discountsEnabled?: boolean } | null }) =>
            state.user != null ? state.user.discountsEnabled === true : false,
        transportCheckEnabled: (state: { user: { transportCheckEnabled?: boolean } | null }) =>
            state.user != null ? state.user.transportCheckEnabled === true : false,
        rootCurrency: (state: { user: { rootCurrency?: string } | null }) =>
            state.user != null && state.user.rootCurrency ? state.user.rootCurrency : '',
        /* rootul logat — Help Center-ul alege documentatia de brand dupa el.
           Lipseste pana cand api_1_0/userData il trimite; atunci se vede doar
           manualul comun, ceea ce e degradarea corecta. */
        rootId: (state: { user: { rootId?: number | string } | null }) => {
            if (state.user == null || state.user.rootId == null) {
                return null;
            }
            const value = Number(state.user.rootId);
            return Number.isFinite(value) ? value : null;
        },
    },
});
